import type { FormStateValue } from '@de/providers/DataSourceManager.types';

/**
 * Whether two form-state entries hold the same selection.
 *
 * Reference equality is what the form-state reducer used to short-circuit on,
 * and it was sufficient while every value was a string. It is not for a list: a
 * control re-emitting the same selection produces a new array each time, so
 * reference equality reports a change, which commits state and re-runs every
 * source keyed on that entry — on every render.
 *
 * Order is significant. Two selections differing only in order are different
 * values, and normalizing here would make the entry read back in an order the
 * control did not choose.
 */
export function formStateValuesEqual(
  a: FormStateValue | undefined,
  b: FormStateValue | undefined,
): boolean {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  return a.length === b.length && a.every((entry, i) => entry === b[i]);
}

/**
 * Read a form-state entry as a list of keys.
 *
 * A list passes through. A string is split on commas, because that is what a
 * multi-value control wrote before form state could hold a list, and stored
 * documents and seeded initial state still carry that shape. Splitting is the
 * lossy direction — a value containing a comma becomes two — which is the reason
 * the list shape exists; this only applies it to values that were already
 * encoded that way and have no other reading.
 */
export function formStateValueToKeys(
  value: FormStateValue | undefined,
): string[] {
  if (value === undefined) return [];
  if (Array.isArray(value)) {
    return value.filter((key) => typeof key === 'string' && key.length > 0);
  }
  if (typeof value !== 'string' || value.length === 0) return [];
  return value
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
}

/**
 * Read a form-state entry as a single value.
 *
 * For a key a single-value control owns. A list arriving here means two controls
 * share a key, or a multi-value control writes one a single-value reader
 * consumes; the first entry is the only defensible reading, and it is better
 * than coercing the whole list to a string that matches nothing.
 */
export function formStateValueToSingle(
  value: FormStateValue | undefined,
): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.find((entry) => typeof entry === 'string');
  }
  return undefined;
}

/**
 * A projection of form state to single values, for a consumer defined only over
 * them.
 *
 * Keeps the narrowing in one place rather than at each read: a caller that needs
 * `formState.geolocation` to be a string should not also have to decide what a
 * list there means.
 */
export function formStateSingles(
  formState: Readonly<Record<string, FormStateValue>>,
): Readonly<Record<string, string>> {
  const singles: Record<string, string> = {};
  for (const key of Object.keys(formState)) {
    const single = formStateValueToSingle(formState[key]);
    if (single !== undefined) singles[key] = single;
  }
  return singles;
}
